import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API } from '../App';
import { Shield, LogOut, Plus, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

function PoliceDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRecordDialog, setShowNewRecordDialog] = useState(false);
  const [formData, setFormData] = useState({
    case_number: '',
    suspect_name: '',
    suspect_id_number: '',
    offense: '',
    date_reported: '',
    status: 'under_investigation'
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData.role !== 'police') {
      navigate('/dashboard');
      return;
    }
    setUser(userData);
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/criminal-records`);
      setRecords(response.data);
    } catch (error) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleSubmitRecord = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/criminal-records`, formData);
      toast.success('Criminal record created successfully!');
      setShowNewRecordDialog(false);
      fetchRecords();
      setFormData({
        case_number: '',
        suspect_name: '',
        suspect_id_number: '',
        offense: '',
        date_reported: '',
        status: 'under_investigation'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    }
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="dashboard" data-testid="police-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img src="/hakicheck-logo.png" alt="HakiCheck Law Enforcement Portal" className="header-logo" />
        </div>
        <div className="dashboard-user">
          <span className="user-name" data-testid="police-name">{user?.full_name}</span>
          <button className="logout-btn" data-testid="logout-btn" onClick={handleLogout}>
            <LogOut size={18} style={{ marginRight: '0.5rem', display: 'inline' }} />
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="dashboard-content">
        <div className="dashboard-welcome">
          <h1 className="welcome-title">Law Enforcement Criminal Records Portal</h1>
          <p className="welcome-subtitle">Manage criminal case records - Free service for law enforcement</p>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-label">Total Records</div>
            <div className="stat-value" data-testid="total-records">{records.length}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Under Investigation</div>
            <div className="stat-value" data-testid="under-investigation">
              {records.filter(r => r.status === 'under_investigation').length}
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Convicted</div>
            <div className="stat-value" data-testid="convicted">
              {records.filter(r => r.status === 'convicted').length}
            </div>
          </div>
        </div>

        {/* Records */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 className="card-title">Criminal Records</h2>
            <Dialog open={showNewRecordDialog} onOpenChange={setShowNewRecordDialog}>
              <DialogTrigger asChild>
                <Button className="btn btn-primary" data-testid="new-record-btn">
                  <Plus size={20} style={{ marginRight: '0.5rem' }} />
                  New Record
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Criminal Record</DialogTitle>
                  <DialogDescription>
                    Enter criminal case details for database entry
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmitRecord} className="space-y-4" data-testid="new-record-form">
                  <div className="form-group">
                    <label>Case Number *</label>
                    <input
                      type="text"
                      data-testid="case-number-input"
                      value={formData.case_number}
                      onChange={(e) => setFormData({...formData, case_number: e.target.value})}
                      required
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div className="form-group">
                    <label>Suspect Name *</label>
                    <input
                      type="text"
                      data-testid="suspect-name-input"
                      value={formData.suspect_name}
                      onChange={(e) => setFormData({...formData, suspect_name: e.target.value})}
                      required
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div className="form-group">
                    <label>Suspect ID Number *</label>
                    <input
                      type="text"
                      data-testid="suspect-id-input"
                      value={formData.suspect_id_number}
                      onChange={(e) => setFormData({...formData, suspect_id_number: e.target.value})}
                      required
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div className="form-group">
                    <label>Offense *</label>
                    <textarea
                      data-testid="offense-input"
                      value={formData.offense}
                      onChange={(e) => setFormData({...formData, offense: e.target.value})}
                      required
                      className="w-full p-2 border rounded"
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Date Reported *</label>
                    <input
                      type="date"
                      data-testid="date-reported-input"
                      value={formData.date_reported}
                      onChange={(e) => setFormData({...formData, date_reported: e.target.value})}
                      required
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      data-testid="status-select"
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      required
                      className="w-full p-2 border rounded"
                    >
                      <option value="under_investigation">Under Investigation</option>
                      <option value="convicted">Convicted</option>
                      <option value="acquitted">Acquitted</option>
                      <option value="pending_trial">Pending Trial</option>
                    </select>
                  </div>

                  <Button type="submit" className="w-full btn btn-primary" data-testid="submit-record-btn">
                    Create Record
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Records List */}
          {records.length === 0 ? (
            <div className="empty-state" data-testid="empty-state">
              <AlertTriangle size={48} style={{ color: '#6c757d', marginBottom: '1rem' }} />
              <p>No criminal records yet.</p>
            </div>
          ) : (
            <div className="records-list">
              {records.map(record => (
                <div key={record.id} className="record-item" data-testid={`record-item-${record.id}`}>
                  <div className="record-header">
                    <h3>{record.suspect_name}</h3>
                    <span className={`status-badge ${record.status}`}>{record.status.replace('_', ' ')}</span>
                  </div>
                  <div className="record-details">
                    <p><strong>Case Number:</strong> {record.case_number}</p>
                    <p><strong>ID Number:</strong> {record.suspect_id_number}</p>
                    <p><strong>Offense:</strong> {record.offense}</p>
                    <p><strong>Date Reported:</strong> {new Date(record.date_reported).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <style>{`
        .loading-screen {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: #6c757d;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #6c757d;
        }

        .records-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .record-item {
          padding: 1.5rem;
          background: #f8f9fa;
          border-radius: 12px;
          border: 1px solid #e9ecef;
        }

        .record-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .record-header h3 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #212529;
          margin: 0;
        }

        .status-badge {
          display: inline-block;
          padding: 0.35rem 0.85rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-badge.under_investigation {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.convicted {
          background: #f8d7da;
          color: #842029;
        }

        .status-badge.acquitted {
          background: #d1e3f0;
          color: #3d5a75;
        }

        .status-badge.pending_trial {
          background: #cfe2ff;
          color: #084298;
        }

        .record-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
          font-size: 0.9rem;
          color: #495057;
        }

        .record-details p {
          margin: 0;
        }

        .record-details strong {
          color: #212529;
        }
      `}</style>
    </div>
  );
}

export default PoliceDashboard;
